import { useState, useEffect } from 'react';
import { Invoice, InvoiceLine, calcularTotales, generarNumeroFactura } from './types';

interface Props {
  invoice?: Invoice | null;
  allInvoices: Invoice[];
  tallerNombre: string;
  tallerNif: string;
  tallerDireccion: string;
  tallerEmail: string;
  tallerTelefono: string;
  onSave: (inv: Invoice) => void;
  onCancel: () => void;
}

const emptyLine = (): InvoiceLine => ({
  id: crypto.randomUUID(),
  descripcion: '',
  cantidad: 1,
  precio_unitario: 0,
  iva: 21,
});

const InvoiceForm = ({ invoice, allInvoices, tallerNombre, tallerNif, tallerDireccion, tallerEmail, tallerTelefono, onSave, onCancel }: Props) => {
  const today = new Date().toISOString().split('T')[0];
  const due = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];

  const [form, setForm] = useState<Omit<Invoice, 'subtotal' | 'total_iva' | 'total'>>(() => {
    if (invoice) {
      const { subtotal: _s, total_iva: _t, total: _tt, ...rest } = invoice;
      return rest;
    }
    return {
      id: crypto.randomUUID(),
      numero: generarNumeroFactura(allInvoices),
      fecha: today,
      fecha_vencimiento: due,
      estado: 'borrador',
      cliente_nombre: '',
      cliente_nif: '',
      cliente_direccion: '',
      cliente_email: '',
      cliente_telefono: '',
      lineas: [emptyLine()],
      notas: '',
    };
  });

  const [lineas, setLineas] = useState<InvoiceLine[]>(invoice?.lineas ?? [emptyLine()]);

  const totales = calcularTotales(lineas);

  const setField = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const updateLinea = (id: string, field: keyof InvoiceLine, value: string | number) => {
    setLineas((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: field === 'descripcion' ? value : Number(value) } : l))
    );
  };

  const addLinea = () => setLineas((p) => [...p, emptyLine()]);
  const removeLinea = (id: string) => setLineas((p) => p.filter((l) => l.id !== id));

  const handleSubmit = () => {
    if (!form.cliente_nombre.trim()) return;
    onSave({ ...form, lineas, ...totales });
  };

  const inputCls = 'w-full bg-zinc-800 border border-zinc-700 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-yellow-400/60 placeholder-zinc-600';
  const labelCls = 'block text-zinc-400 text-xs font-semibold mb-1 uppercase tracking-wide';

  return (
    <div className="space-y-6">
      {/* Header info */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className={labelCls}>Nº Factura</label>
          <input className={inputCls} value={form.numero} onChange={(e) => setField('numero', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Fecha emisión</label>
          <input type="date" className={inputCls} value={form.fecha} onChange={(e) => setField('fecha', e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Fecha vencimiento</label>
          <input type="date" className={inputCls} value={form.fecha_vencimiento} onChange={(e) => setField('fecha_vencimiento', e.target.value)} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Estado</label>
          <select
            className={inputCls}
            value={form.estado}
            onChange={(e) => setField('estado', e.target.value as Invoice['estado'])}
          >
            <option value="borrador">Borrador</option>
            <option value="emitida">Emitida</option>
            <option value="pagada">Pagada</option>
            <option value="cancelada">Cancelada</option>
          </select>
        </div>
      </div>

      {/* Cliente */}
      <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          <i className="ri-user-3-line text-yellow-400" /> Datos del cliente
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Nombre / Empresa *</label>
            <input className={inputCls} placeholder="Nombre completo o razón social" value={form.cliente_nombre} onChange={(e) => setField('cliente_nombre', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>NIF / CIF</label>
            <input className={inputCls} placeholder="12345678A" value={form.cliente_nif} onChange={(e) => setField('cliente_nif', e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Dirección</label>
            <input className={inputCls} placeholder="Calle, número, ciudad, CP" value={form.cliente_direccion} onChange={(e) => setField('cliente_direccion', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" className={inputCls} placeholder="cliente@email.com" value={form.cliente_email} onChange={(e) => setField('cliente_email', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Teléfono</label>
            <input className={inputCls} placeholder="+34 600 000 000" value={form.cliente_telefono} onChange={(e) => setField('cliente_telefono', e.target.value)} />
          </div>
        </div>
      </div>

      {/* Líneas */}
      <div className="bg-zinc-800/50 rounded-xl p-4 space-y-3">
        <h3 className="text-white font-bold text-sm flex items-center gap-2">
          <i className="ri-list-check-2 text-yellow-400" /> Conceptos / Servicios
        </h3>

        {/* Header */}
        <div className="hidden sm:grid grid-cols-12 gap-2 text-zinc-500 text-[10px] font-bold uppercase tracking-wider px-1">
          <div className="col-span-5">Descripción</div>
          <div className="col-span-2 text-center">Cant.</div>
          <div className="col-span-2 text-center">Precio unit.</div>
          <div className="col-span-2 text-center">IVA %</div>
          <div className="col-span-1" />
        </div>

        <div className="space-y-2">
          {lineas.map((l) => (
            <div key={l.id} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-12 sm:col-span-5">
                <input
                  className={inputCls}
                  placeholder="Descripción del servicio..."
                  value={l.descripcion}
                  onChange={(e) => updateLinea(l.id, 'descripcion', e.target.value)}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <input
                  type="number"
                  min="1"
                  className={`${inputCls} text-center`}
                  value={l.cantidad}
                  onChange={(e) => updateLinea(l.id, 'cantidad', e.target.value)}
                />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className={`${inputCls} pr-6`}
                    value={l.precio_unitario}
                    onChange={(e) => updateLinea(l.id, 'precio_unitario', e.target.value)}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">€</span>
                </div>
              </div>
              <div className="col-span-3 sm:col-span-2">
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className={`${inputCls} pr-6`}
                    value={l.iva}
                    onChange={(e) => updateLinea(l.id, 'iva', e.target.value)}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">%</span>
                </div>
              </div>
              <div className="col-span-1 flex justify-center">
                <button
                  onClick={() => removeLinea(l.id)}
                  disabled={lineas.length === 1}
                  className="w-8 h-8 flex items-center justify-center text-zinc-600 hover:text-red-400 disabled:opacity-30 cursor-pointer transition-colors"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={addLinea}
          className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 text-xs font-semibold cursor-pointer transition-colors mt-1"
        >
          <i className="ri-add-circle-line text-base" /> Añadir línea
        </button>

        {/* Totales */}
        <div className="border-t border-zinc-700 pt-3 mt-3 space-y-1.5 text-sm">
          <div className="flex justify-between text-zinc-400">
            <span>Subtotal (sin IVA)</span>
            <span className="font-semibold text-white">{totales.subtotal.toFixed(2)} €</span>
          </div>
          <div className="flex justify-between text-zinc-400">
            <span>IVA</span>
            <span className="font-semibold text-white">{totales.total_iva.toFixed(2)} €</span>
          </div>
          <div className="flex justify-between text-white font-black text-base border-t border-zinc-700 pt-2 mt-2">
            <span>TOTAL</span>
            <span className="text-yellow-400">{totales.total.toFixed(2)} €</span>
          </div>
        </div>
      </div>

      {/* Notas */}
      <div>
        <label className={labelCls}>Notas / Condiciones de pago</label>
        <textarea
          className={`${inputCls} resize-none`}
          rows={3}
          placeholder="Ej: Pago a 30 días. Transferencia bancaria a IBAN ES00 0000..."
          value={form.notas}
          onChange={(e) => setField('notas', e.target.value)}
          maxLength={500}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          onClick={onCancel}
          className="px-5 py-2.5 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-600 text-sm font-semibold rounded-lg cursor-pointer transition-all whitespace-nowrap"
        >
          Cancelar
        </button>
        <button
          onClick={handleSubmit}
          disabled={!form.cliente_nombre.trim()}
          className="px-6 py-2.5 bg-yellow-400 text-black text-sm font-black rounded-lg hover:bg-yellow-300 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-all whitespace-nowrap flex items-center gap-2"
        >
          <i className="ri-save-line" /> Guardar factura
        </button>
      </div>
    </div>
  );
};

export default InvoiceForm;
